import { useEffect } from 'react';

import { BookIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useDialogAlert from '@/hooks/useDialogAlert';
import { selectNodeIdByActionId, selectSelectedAction } from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import {
  Action,
  SWITCH_CHANNEL_DESTINATIONS,
  SwitchChannelDestination,
} from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { SWITCH_CHANNEL_DOCS_URL } from '@/util/constants';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { switchChannelName } from '@/util/switchChannel';
import { switchChannelActionSchema } from '@/util/validation/schemas/action-schemas';

import ToolkitWrapper from '../../ToolkitWrapper';

type Form = { channel: string };

const ToolkitActionSwitchChannel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { action_id, channel, type } = useSelector(
    (state: RootState) =>
      selectSelectedAction(state) as Extract<Action, { type: 'switch_channel' }>,
    shallowEqual
  );
  const parentNodeId = useSelector(selectNodeIdByActionId(action_id));

  const form = useForm<Form>({
    mode: 'onChange',
    defaultValues: { channel: channel ?? '' },
    resolver: createYupResolver<Form>(switchChannelActionSchema),
  });
  const {
    trigger,
    formState: { errors },
  } = form;

  const { setFieldError } = useDialogAlert({
    id: action_id,
    nodeId: parentNodeId,
    title: t('actions.types.switch_channel'),
    type,
  });

  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    setFieldError('channel', errors.channel?.message);
  }, [errors.channel?.message, setFieldError]);

  return (
    <ToolkitWrapper type="switch_channel">
      <Form {...form}>
        <div className="flex flex-col gap-4 pb-6">
          <FormField
            control={form.control}
            name="channel"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dialog.switch_channel.channel')}</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    dispatch(
                      updateAction({
                        actionId: action_id,
                        action: { channel: value as SwitchChannelDestination },
                      })
                    );
                  }}
                >
                  <FormControl>
                    <SelectTrigger
                      className="w-full"
                      aria-label={t('dialog.switch_channel.channel')}
                    >
                      <SelectValue placeholder={t('dialog.switch_channel.select_channel')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {SWITCH_CHANNEL_DESTINATIONS.map((destination) => (
                      <SelectItem key={destination} value={destination}>
                        {switchChannelName(t, destination)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Form>
      <ContextualHelp
        preventClose
        name="action-switch-channel"
        title={t('dialog.switch_channel.title')}
        links={[
          { label: t('docs.docs'), url: SWITCH_CHANNEL_DOCS_URL, icon: <BookIcon size={16} /> },
        ]}
      >
        <p>{t('dialog.switch_channel.body')}</p>
        {channel === 'web' && <p className="mt-2">{t('dialog.switch_channel.body_web')}</p>}
      </ContextualHelp>
    </ToolkitWrapper>
  );
};

export default ToolkitActionSwitchChannel;
